import React, { Component } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Badge,
  InputGroup,
  Pagination,
  PaginationItem,
  PaginationLink
} from 'reactstrap';
import { useAuth } from '../contexts/AuthContext';
import ProtectedRoute from './ProtectedRoute';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
const DEFAULT_PAGE_SIZE = 10;
const HIDE_UNPAID_STORAGE_KEY = 'workActs.hideUnpaid';
const PAGE_SIZE_STORAGE_KEY = 'workActs.pageSize';

const readHideUnpaid = () => {
  try {
    const raw = window.localStorage.getItem(HIDE_UNPAID_STORAGE_KEY);
    if (raw === null) return true;
    return raw === 'true';
  } catch {
    return true;
  }
};

const readPageSize = () => {
  try {
    const raw = window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    const n = parseInt(raw, 10);
    return PAGE_SIZE_OPTIONS.includes(n) ? n : DEFAULT_PAGE_SIZE;
  } catch {
    return DEFAULT_PAGE_SIZE;
  }
};

const WorkCategoryUnit = {
  Unknown: 0,
  Money: 1,
  Pieces: 2,
  Time: 3,
  Percent: 4
};

const getUnitMeta = (unit) => {
  switch (unit) {
    case WorkCategoryUnit.Money:
      return { inputLabel: '₽', rateSuffix: '₽' };
    case WorkCategoryUnit.Pieces:
      return { inputLabel: 'шт', rateSuffix: '₽/шт' };
    case WorkCategoryUnit.Percent:
      return { inputLabel: '₽', rateSuffix: '%' };
    case WorkCategoryUnit.Time:
    default:
      return { inputLabel: 'время', rateSuffix: '₽/ч' };
  }
};

// Кастомные стили для консистентности
const customStyles = {
  badge: {
    draft: {
      backgroundColor: '#6c757d',
      color: 'white',
      border: 'none'
    },
    formed: {
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none'
    },
    paid: {
      backgroundColor: '#28a745',
      color: 'white',
      border: 'none'
    }
  },
  button: {
    view: {
      backgroundColor: 'transparent',
      borderColor: '#17a2b8',
      color: '#17a2b8'
    },
    edit: {
      backgroundColor: 'transparent',
      borderColor: '#007bff',
      color: '#007bff'
    },
    form: {
      backgroundColor: 'transparent',
      borderColor: '#28a745',
      color: '#28a745'
    },
    pay: {
      backgroundColor: 'transparent',
      borderColor: '#ffc107',
      color: '#ffc107'
    },
    recalculate: {
      backgroundColor: 'transparent',
      borderColor: '#6f42c1',
      color: '#6f42c1'
    },
    delete: {
      backgroundColor: 'transparent',
      borderColor: '#dc3545',
      color: '#dc3545'
    }
  }
};

// Wrapper component to use hooks in class component
const WorkActManagementWithAuth = (props) => {
  const auth = useAuth();
  return (
    <ProtectedRoute requiredRoles={["Manager", "Admin"]}>
      <WorkActManagement {...props} auth={auth} />
    </ProtectedRoute>
  );
};

class WorkActManagement extends Component {
  static displayName = WorkActManagement.name;

  constructor(props) {
    super(props);
    this.state = {
      workActs: [],
      employees: [],
      workCategories: [],
      qualifications: [],
      loading: true,
      error: null,
      successMessage: null,
      createModal: false,
      editModal: false,
      viewModal: false,
      selectedWorkAct: null,
      searchTerm: '',
      statusFilter: '',
      hideUnpaid: readHideUnpaid(),
      page: 1,
      pageSize: readPageSize(),
      totalCount: 0,
      formData: {
        employeeId: '',
        actDate: '',
        workRecords: [],
        notes: ''
      },
      formErrors: {}
    };
  }

  sanitizeNonNegativeInt = (rawValue, maxValue = null) => {
    if (rawValue === '' || rawValue === null || rawValue === undefined) return '';
    const parsed = parseInt(String(rawValue), 10);
    if (Number.isNaN(parsed)) return '';
    const clamped = Math.max(0, parsed);
    if (maxValue !== null && clamped > maxValue) return String(maxValue);
    return String(clamped);
  };

  async componentDidMount() {
    await this.loadWorkActs();
    await this.loadEmployees();
    await this.loadWorkCategories();
    await this.loadQualifications();
  }

  componentWillUnmount() {
    if (this._searchDebounce) clearTimeout(this._searchDebounce);
  }

  onSearchChange = (value) => {
    this.setState({ searchTerm: value });
    if (this._searchDebounce) clearTimeout(this._searchDebounce);
    this._searchDebounce = setTimeout(() => this.reloadFromFirstPage(), 350);
  };

  loadWorkActs = async () => {
    try {
      this.setState({ loading: true });
      const { page, pageSize, searchTerm, statusFilter, hideUnpaid } = this.state;
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('pageSize', String(pageSize));
      if (searchTerm.trim()) params.append('search', searchTerm.trim());

      let statuses = [];
      if (statusFilter !== '') {
        statuses = [statusFilter];
      } else if (hideUnpaid) {
        statuses = ['0', '1'];
      }
      statuses.forEach(s => params.append('statuses', s));

      const response = await this.props.auth.authenticatedFetch(`/api/workacts/paged?${params.toString()}`);

      if (response && response.ok) {
        const data = await response.json();
        this.setState({
          workActs: data.items || [],
          totalCount: data.totalCount || 0,
          loading: false
        });
      } else if (response) {
        this.setState({ error: 'Не удалось загрузить акты работ', loading: false });
      }
    } catch (error) {
      this.setState({ error: 'Ошибка сети', loading: false });
    }
  };

  reloadFromFirstPage = () => {
    this.setState({ page: 1 }, () => { this.loadWorkActs(); });
  };

  goToPage = (p) => {
    this.setState({ page: p }, () => { this.loadWorkActs(); });
  };

  loadEmployees = async () => {
    try {
      const response = await this.props.auth.authenticatedFetch('/api/employees');
      
      if (response && response.ok) {
        const employees = await response.json();
        this.setState({ employees });
      }
    } catch (error) {
      console.error('Failed to load employees:', error);
    }
  };

  loadWorkCategories = async () => {
    try {
      const response = await this.props.auth.authenticatedFetch('/api/workcategories');

      if (response && response.ok) {
        const workCategories = await response.json();
        this.setState({ workCategories });
      }
    } catch (error) {
      console.error('Failed to load work categories:', error);
    }
  };

  loadQualifications = async () => {
    try {
      const response = await this.props.auth.authenticatedFetch('/api/qualifications');

      if (response && response.ok) {
        const qualifications = await response.json();
        this.setState({ qualifications });
      }
    } catch (error) {
      console.error('Failed to load qualifications:', error);
    }
  };

  openCreateModal = () => {
    this.setState({
      createModal: true,
      formData: {
        employeeId: '',
        actDate: new Date().toISOString().split('T')[0],
        workRecords: [],
        notes: ''
      },
      formErrors: {},
      error: null,
      successMessage: null
    });
  };

  openEditModal = (workAct) => {
    this.setState({
      editModal: true,
      selectedWorkAct: workAct,
      formData: {
        employeeId: workAct.employeeId,
        actDate: workAct.actDate ? this.formatDateForInput(workAct.actDate) : '',
        workRecords: workAct.workRecords || [],
        notes: workAct.notes || ''
      },
      formErrors: {},
      error: null,
      successMessage: null
    });
  };

  closeModals = () => {
    this.setState({
      createModal: false,
      editModal: false,
      viewModal: false,
      selectedWorkAct: null,
      formErrors: {}
    });
  };

  openViewModal = (workAct) => {
    this.setState({
      viewModal: true,
      selectedWorkAct: workAct,
      error: null,
      successMessage: null
    });
  };

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState({
      formData: {
        ...this.state.formData,
        [name]: value
      }
    });
  };

  addWorkRecord = () => {
    const { workRecords } = this.state.formData;
    this.setState({
      formData: {
        ...this.state.formData,
        workRecords: [{
          workCategoryId: '',
          hours: '',
          minutes: '',
          comment: '',
          quantity: ''
        }, ...workRecords]
      }
    });
  };

  removeWorkRecord = (index) => {
    const { workRecords } = this.state.formData;
    const newWorkRecords = workRecords.filter((_, i) => i !== index);
    this.setState({
      formData: {
        ...this.state.formData,
        workRecords: newWorkRecords
      }
    });
  };

  updateWorkRecord = (index, field, value) => {
    const { workRecords } = this.state.formData;
    const newWorkRecords = [...workRecords];
    newWorkRecords[index] = {
      ...newWorkRecords[index],
      [field]: value
    };
    this.setState({
      formData: {
        ...this.state.formData,
        workRecords: newWorkRecords
      }
    });
  };

  calculateRecordAmount = (record, employeeId) => {
    if (!record.workCategoryId) {
      return 0;
    }

    // Get work category to check unit
    const workCategory = this.state.workCategories.find(cat => cat.id === record.workCategoryId);
    if (!workCategory) {
      return 0;
    }

    const unit = workCategory.unit ?? WorkCategoryUnit.Time;

    // Money: amount is entered directly
    if (unit === WorkCategoryUnit.Money) {
      return parseFloat(record.quantity) || 0;
    }

    // Percent: amount × tariff / 100
    if (unit === WorkCategoryUnit.Percent) {
      const baseSum = parseInt(record.quantity) || 0;
      if (baseSum === 0) return 0;

      if (!employeeId) return 0;
      const employee = this.state.employees.find(emp => emp.id === employeeId);
      if (!employee) return 0;

      let tariff = 0;
      if (employee.qualifications) {
        const eq = employee.qualifications.find(q => q.workCategoryId === record.workCategoryId);
        if (eq) tariff = eq.tariff;
      }
      if (tariff === 0) {
        const dq = this.state.qualifications.find(q =>
          q.workCategoryId === record.workCategoryId && q.name === "По умолчанию"
        );
        if (dq) tariff = dq.tariff;
      }

      return baseSum * tariff / 100;
    }

    // Pieces: quantity × tariff
    if (unit === WorkCategoryUnit.Pieces) {
      const quantity = parseInt(record.quantity) || 0;
      if (quantity === 0) return 0;

      if (!employeeId) return 0;
      const employee = this.state.employees.find(emp => emp.id === employeeId);
      if (!employee) return 0;

      let tariff = 0;
      if (employee.qualifications) {
        const employeeQualification = employee.qualifications.find(q => q.workCategoryId === record.workCategoryId);
        if (employeeQualification) {
          tariff = employeeQualification.tariff;
        }
      }

      if (tariff === 0) {
        const defaultQualification = this.state.qualifications.find(q =>
          q.workCategoryId === record.workCategoryId && q.name === "По умолчанию"
        );
        if (defaultQualification) {
          tariff = defaultQualification.tariff;
        }
      }

      return quantity * tariff;
    }

    // Time: calculate based on time and rates
    if (!employeeId) {
      return 0;
    }

    // Get selected employee
    const employee = this.state.employees.find(emp => emp.id === employeeId);
    if (!employee) {
      return 0;
    }

    // Calculate total hours
    const hours = parseInt(record.hours) || 0;
    const minutes = parseInt(record.minutes) || 0;
    const totalHours = hours + (minutes / 60);

    if (totalHours === 0) {
      return 0;
    }

    // Find qualification for this work category
    let tariff = 0;

    // First, check if employee has a specific qualification for this category
    if (employee.qualifications) {
      const employeeQualification = employee.qualifications.find(q => q.workCategoryId === record.workCategoryId);
      if (employeeQualification) {
        tariff = employeeQualification.tariff;
      }
    }

    // If no specific qualification found, try to find default qualification
    if (tariff === 0) {
      const defaultQualification = this.state.qualifications.find(q =>
        q.workCategoryId === record.workCategoryId && q.name === "По умолчанию"
      );
      if (defaultQualification) {
        tariff = defaultQualification.tariff;
      }
    }

    return totalHours * tariff;
  };

  calculateTotalAmount = (workRecords, employeeId) => {
    if (!workRecords || workRecords.length === 0) {
      return 0;
    }

    return workRecords.reduce((total, record) => {
      return total + this.calculateRecordAmount(record, employeeId);
    }, 0);
  };

  validateForm = () => {
    const { formData } = this.state;
    const errors = {};

    if (!formData.employeeId) {
      errors.employeeId = 'Сотрудник обязателен';
    }

    if (!formData.actDate) {
      errors.actDate = 'Дата формирования акта обязательна';
    }

    if (formData.workRecords.length === 0) {
      errors.workRecords = 'Добавьте хотя бы одну запись о работе';
    }

    // Validate work records
    formData.workRecords.forEach((record, index) => {
      if (!record.workCategoryId) {
        errors[`workRecord_${index}_category`] = 'Выберите вид работ';
        return;
      }

      // Get work category to check unit
      const workCategory = this.state.workCategories.find(cat => cat.id === record.workCategoryId);
      if (!workCategory) {
        errors[`workRecord_${index}_category`] = 'Неверный вид работ';
        return;
      }

      const unit = workCategory.unit ?? WorkCategoryUnit.Time;

      if (unit === WorkCategoryUnit.Money) {
        const amount = parseInt(record.quantity) || 0;
        if (amount === 0) errors[`workRecord_${index}_amount`] = 'Укажите сумму выплаты';
      } else if (unit === WorkCategoryUnit.Percent) {
        const amount = parseInt(record.quantity) || 0;
        if (amount === 0) errors[`workRecord_${index}_amount`] = 'Укажите сумму (₽)';
      } else if (unit === WorkCategoryUnit.Pieces) {
        const qty = parseInt(record.quantity) || 0;
        if (qty === 0) errors[`workRecord_${index}_amount`] = 'Укажите количество (шт)';
      } else {
        const hours = parseInt(record.hours) || 0;
        const minutes = parseInt(record.minutes) || 0;
        if (hours === 0 && minutes === 0) errors[`workRecord_${index}_time`] = 'Укажите время работы';
      }
    });

    this.setState({ formErrors: errors });
    return Object.keys(errors).length === 0;
  };

  saveWorkAct = async () => {
    if (!this.validateForm()) return;

    try {
      const { formData, selectedWorkAct } = this.state;
      const isEdit = !!selectedWorkAct;

      const requestData = {
        employeeId: formData.employeeId,
        actDate: formData.actDate,
        workRecords: formData.workRecords.map(record => {
          const workCategory = this.state.workCategories.find(cat => cat.id === record.workCategoryId);
          const unit = workCategory?.unit ?? WorkCategoryUnit.Time;
          return {
            workCategoryId: record.workCategoryId,
            hours: unit === WorkCategoryUnit.Time ? (parseInt(record.hours) || 0) : 0,
            minutes: unit === WorkCategoryUnit.Time ? (parseInt(record.minutes) || 0) : 0,
            comment: record.comment,
            quantity: unit === WorkCategoryUnit.Time ? null : (parseInt(record.quantity) || 0)
          };
        }),
        notes: formData.notes || null
      };

      const url = isEdit ? `/api/workacts/${selectedWorkAct.id}` : '/api/workacts';
      const method = isEdit ? 'PUT' : 'POST';

      const response = await this.props.auth.authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      if (response && response.ok) {
        await this.loadWorkActs();
        this.closeModals();

        // Show notification if act was reset to draft during edit
        if (isEdit && selectedWorkAct.status !== 0) {
          this.setState({
            error: null,
            successMessage: 'Акт сохранен. Статус изменен на "Черновик" из-за изменения данных.'
          });
          // Clear success message after 5 seconds
          setTimeout(() => {
            this.setState({ successMessage: null });
          }, 5000);
        }
      } else if (response) {
        const errorData = await response.json();
        this.setState({ error: errorData.message || 'Не удалось сохранить акт работ' });
      }
    } catch (error) {
      this.setState({ error: 'Ошибка сети' });
    }
  };

  deleteWorkAct = async (workActId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот акт работ?')) {
      return;
    }

    try {
      const response = await this.props.auth.authenticatedFetch(`/api/workacts/${workActId}`, {
        method: 'DELETE'
      });

      if (response && response.ok) {
        await this.loadWorkActs();
      } else if (response) {
        this.setState({ error: 'Не удалось удалить акт работ' });
      }
    } catch (error) {
      this.setState({ error: 'Ошибка сети' });
    }
  };

  changeStatus = async (workActId, newStatus) => {
    try {
      const response = await this.props.auth.authenticatedFetch(`/api/workacts/${workActId}/change-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (response && response.ok) {
        await this.loadWorkActs();
      } else if (response) {
        this.setState({ error: 'Не удалось изменить статус акта' });
      }
    } catch (error) {
      this.setState({ error: 'Ошибка сети' });
    }
  };

  recalculateWorkAct = async (workActId) => {
    if (!window.confirm('Пересчитать акт с текущими ставками? Акт будет переведен в статус "Черновик".')) {
      return;
    }

    try {
      const response = await this.props.auth.authenticatedFetch(`/api/workacts/${workActId}/recalculate`, {
        method: 'POST'
      });

      if (response && response.ok) {
        await this.loadWorkActs();
        this.setState({
          error: null,
          successMessage: 'Акт пересчитан с актуальными ставками. Статус изменен на "Черновик".'
        });
        // Clear success message after 5 seconds
        setTimeout(() => {
          this.setState({ successMessage: null });
        }, 5000);
      } else if (response) {
        const errorData = await response.json();
        this.setState({ error: errorData.message || 'Не удалось пересчитать акт' });
      }
    } catch (error) {
      this.setState({ error: 'Ошибка сети' });
    }
  };

  formatDate = (dateString) => {
    if (!dateString) return 'Не указана';

    try {
      // Если дата уже содержит время, используем как есть
      if (dateString.includes('T')) {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU');
      }

      // Если дата только в формате YYYY-MM-DD, добавляем время
      const date = new Date(dateString + 'T00:00:00');
      return date.toLocaleDateString('ru-RU');
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'Неверная дата';
    }
  };

  formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // Для input type="date" нужен формат YYYY-MM-DD в локальном времени
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  getStatusBadge = (status) => {
    const statusMap = {
      0: { style: customStyles.badge.draft, text: 'Черновик' },
      1: { style: customStyles.badge.formed, text: 'Сформирован' },
      2: { style: customStyles.badge.paid, text: 'Оплачен' }
    };
    
    const statusInfo = statusMap[status] || statusMap[0];
    return (
      <Badge style={statusInfo.style}>
        {statusInfo.text}
      </Badge>
    );
  };

  renderViewModal = () => {
    const { viewModal, selectedWorkAct } = this.state;

    if (!selectedWorkAct) return null;

    const formatDateTime = (dateString) => {
      if (!dateString) return 'Не указана';
      return new Date(dateString).toLocaleString('ru-RU');
    };

    const formatTime = (hours, minutes) => {
      if (hours === 0 && minutes === 0) return '0 ч';
      if (minutes === 0) return `${hours} ч`;
      if (hours === 0) return `${minutes} мин`;
      return `${hours} ч ${minutes} мин`;
    };

    return (
      <Modal isOpen={viewModal} toggle={this.closeModals} size="xl">
        <ModalHeader toggle={this.closeModals} className="bg-dark text-white">
          📋 Просмотр акта выполненных работ
        </ModalHeader>
        <ModalBody className="p-4">
          {/* Header Information */}
          <Row className="mb-4">
            <Col md={6}>
              <Card className="border-secondary">
                <CardHeader className="bg-light border-bottom">
                  <h5 className="mb-0 text-dark">📋 Информация об акте</h5>
                </CardHeader>
                <CardBody>
                  <div className="mb-2">
                    <strong>Номер акта:</strong> <span className="text-primary fs-5">{selectedWorkAct.actNumber}</span>
                  </div>
                  <div className="mb-2">
                    <strong>Дата формирования:</strong> {this.formatDate(selectedWorkAct.actDate)}
                  </div>
                  <div className="mb-2">
                    <strong>Дата оплаты:</strong> {this.formatDate(selectedWorkAct.paymentDate)}
                  </div>
                  <div className="mb-2">
                    <strong>Статус:</strong> {this.getStatusBadge(selectedWorkAct.status)}
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="border-secondary">
                <CardHeader className="bg-light border-bottom">
                  <h5 className="mb-0 text-dark">👤 Информация о сотруднике</h5>
                </CardHeader>
                <CardBody>
                  <div className="mb-2">
                    <strong>ФИО:</strong> {selectedWorkAct.employee?.fullName || 'Неизвестно'}
                  </div>
                  <div className="mb-2">
                    <strong>Телефон:</strong> {selectedWorkAct.employee?.phone || 'Не указан'}
                  </div>
                  <div className="mb-2">
                    <strong>Дата найма:</strong> {formatDateTime(selectedWorkAct.employee?.hireDate)}
                  </div>
                  <div className="mb-2">
                    <strong>Стаж:</strong> {selectedWorkAct.employee?.workExperienceMonths ?
                      `${Math.floor(selectedWorkAct.employee.workExperienceMonths / 12)} г. ${selectedWorkAct.employee.workExperienceMonths % 12} мес.` :
                      'Не указан'}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Work Records */}
          <Card className="border-secondary mb-4">
            <CardHeader className="bg-light border-bottom">
              <h5 className="mb-0 text-dark">📋 Выполненные работы</h5>
            </CardHeader>
            <CardBody className="p-0">
              <Table className="mb-0" striped>
                <thead className="table-light">
                  <tr>
                    <th>№</th>
                    <th>📁 Вид работ</th>
                    <th>🎯 Квалификация</th>
                    <th>💰 Тариф</th>
                    <th>📏 Объем</th>
                    <th>💵 Сумма</th>
                    <th>💬 Комментарий</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedWorkAct.workRecords?.map((record, index) => (
                    <tr key={index}>
                      {(() => {
                        const unit = record.workCategory?.unit ?? WorkCategoryUnit.Time;
                        const unitMeta = getUnitMeta(unit);
                        const volume =
                          unit === WorkCategoryUnit.Money ? `${Math.round(record.quantity || 0).toLocaleString()} ₽`
                            : unit === WorkCategoryUnit.Percent ? `${Math.round(record.quantity || 0).toLocaleString()} ₽`
                              : unit === WorkCategoryUnit.Pieces ? `${Math.round(record.quantity || 0).toLocaleString()} шт`
                                : formatTime(record.hours, record.minutes);

                        return (
                          <>
                      <td className="fw-bold">{index + 1}</td>
                      <td>
                        <span className="badge text-dark me-1">
                          {record.workCategory?.name || 'Неизвестно'}
                        </span>
                      </td>
                      <td>
                        <span className="badge text-dark">
                          {unit === WorkCategoryUnit.Money ? '—' : (record.usedQualification?.name || 'По умолчанию')}
                        </span>
                      </td>
                      <td className="text-end">
                        <strong>
                          {unit === WorkCategoryUnit.Money ? '—' : `${Math.round(record.tariff || 0).toLocaleString()} ${unitMeta.rateSuffix}`}
                        </strong>
                      </td>
                      <td className="text-center">
                        <span className="badge text-dark">
                          {volume}
                        </span>
                      </td>
                      <td className="text-end">
                        <strong className="text-success">
                          {Math.round(record.totalAmount || 0).toLocaleString()} ₽
                        </strong>
                      </td>
                      <td>
                        <small className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                          {record.comment || '—'}
                        </small>
                      </td>
                          </>
                        );
                      })()}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>

          {/* Summary */}
          <Row>
            <Col md={6}>
              <Card className="border-secondary">
                <CardHeader className="bg-light border-bottom">
                  <h5 className="mb-0 text-dark">📊 Итоги</h5>
                </CardHeader>
                <CardBody>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Общее время:</span>
                    <strong>{selectedWorkAct.totalHours?.toFixed(1)} часов</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Количество записей:</span>
                    <strong>{selectedWorkAct.workRecords?.length || 0}</strong>
                  </div>
                  <hr />
                  <div className="d-flex justify-content-between">
                    <span className="fs-5">Общая сумма:</span>
                    <strong className="fs-4 text-success">
                      {Math.round(selectedWorkAct.totalAmount || 0).toLocaleString()} ₽
                    </strong>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              {selectedWorkAct.notes && (
                <Card className="border-secondary">
                  <CardHeader className="bg-light border-bottom">
                    <h5 className="mb-0 text-dark">📝 Заметки</h5>
                  </CardHeader>
                  <CardBody>
                    <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{selectedWorkAct.notes}</p>
                  </CardBody>
                </Card>
              )}

              {selectedWorkAct.employee?.notes && (
                <Card className="border-secondary mt-3">
                  <CardHeader className="bg-light border-bottom">
                    <h5 className="mb-0 text-dark">👤 Заметка о сотруднике</h5>
                  </CardHeader>
                  <CardBody>
                    <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{selectedWorkAct.employee.notes}</p>
                  </CardBody>
                </Card>
              )}

              <Card className="border-light mt-3">
                <CardBody className="text-muted small">
                  <div>Создан: {formatDateTime(selectedWorkAct.createdAt)} ({selectedWorkAct.createdBy})</div>
                  <div>Изменен: {formatDateTime(selectedWorkAct.updatedAt)} ({selectedWorkAct.updatedBy})</div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={this.closeModals}>
            Закрыть
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  renderWorkActModal = () => {
    const { createModal, editModal, formData, formErrors, employees, workCategories } = this.state;
    const isEdit = !!editModal;

    return (
      <Modal isOpen={createModal || editModal} toggle={this.closeModals} size="xl">
        <ModalHeader toggle={this.closeModals}>
          {isEdit ? 'Редактировать акт работ' : 'Создать акт работ'}
        </ModalHeader>
        <ModalBody>
          <Form>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="employeeId">Сотрудник *</Label>
                  <Input
                    type="select"
                    name="employeeId"
                    id="employeeId"
                    value={formData.employeeId}
                    onChange={this.handleInputChange}
                    invalid={!!formErrors.employeeId}
                  >
                    <option value="">Выберите сотрудника</option>
                    {employees.map(employee => (
                      <option key={employee.id} value={employee.id}>
                        {employee.fullName}
                      </option>
                    ))}
                  </Input>
                  {formErrors.employeeId && <div className="invalid-feedback">{formErrors.employeeId}</div>}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="actDate">Дата формирования акта *</Label>
                  <Input
                    type="date"
                    name="actDate"
                    id="actDate"
                    value={formData.actDate}
                    onChange={this.handleInputChange}
                    invalid={!!formErrors.actDate}
                  />
                  {formErrors.actDate && <div className="invalid-feedback">{formErrors.actDate}</div>}
                </FormGroup>
              </Col>
            </Row>



            <FormGroup className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Label className="mb-0">📋 Записи о выполненных работах</Label>
                <Button color="success" size="sm" onClick={this.addWorkRecord}>
                  ➕ Добавить запись
                </Button>
              </div>

              {formErrors.workRecords && (
                <Alert color="danger" className="mb-3">{formErrors.workRecords}</Alert>
              )}

              {formData.workRecords.length === 0 ? (
                <div className="text-center p-3 text-muted">
                  Нажмите "Добавить запись" чтобы добавить запись о работе
                </div>
              ) : (
                <Table className="mb-0" responsive>
                  <thead className="table-light">
                    <tr>
                      <th width="5%">#</th>
                      <th width="25%">Вид работ *</th>
                      <th width="20%">Объем</th>
                      <th width="15%">Итого</th>
                      <th width="25%">Комментарий</th>
                      <th width="10%">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.workRecords.map((record, index) => (
                      <tr key={index}>
                        <td className="align-middle">
                          <Badge color="secondary">{index + 1}</Badge>
                        </td>
                        <td>
                          <Input
                            type="select"
                            value={record.workCategoryId}
                            onChange={(e) => this.updateWorkRecord(index, 'workCategoryId', e.target.value)}
                            invalid={!!formErrors[`workRecord_${index}_category`]}
                            size="sm"
                          >
                            <option value="">Выберите вид работ</option>
                            {workCategories.map(category => (
                              <option key={category.id} value={category.id}>
                                {category.name}
                              </option>
                            ))}
                          </Input>
                          {formErrors[`workRecord_${index}_category`] && (
                            <div className="invalid-feedback d-block">{formErrors[`workRecord_${index}_category`]}</div>
                          )}
                        </td>
                        <td>
                          {(() => {
                            const workCategory = workCategories.find(cat => cat.id === record.workCategoryId);
                            const unit = workCategory?.unit ?? WorkCategoryUnit.Time;
                            const unitMeta = getUnitMeta(unit);

                            if (unit === WorkCategoryUnit.Time) {
                              return (
                                <div>
                                  <div className="d-flex gap-1">
                                    <Input
                                      type="number"
                                      placeholder="ч"
                                      min="0"
                                      step="1"
                                      value={record.hours}
                                      onChange={(e) => this.updateWorkRecord(index, 'hours', this.sanitizeNonNegativeInt(e.target.value))}
                                      invalid={!!formErrors[`workRecord_${index}_time`]}
                                      size="sm"
                                      style={{ width: '60px' }}
                                    />
                                    <Input
                                      type="number"
                                      placeholder="мин"
                                      min="0"
                                      max="60"
                                      step="1"
                                      value={record.minutes}
                                      onChange={(e) => this.updateWorkRecord(index, 'minutes', this.sanitizeNonNegativeInt(e.target.value, 60))}
                                      invalid={!!formErrors[`workRecord_${index}_time`]}
                                      size="sm"
                                      style={{ width: '60px' }}
                                    />
                                  </div>
                                  {formErrors[`workRecord_${index}_time`] && (
                                    <div className="invalid-feedback d-block">{formErrors[`workRecord_${index}_time`]}</div>
                                  )}
                                </div>
                              );
                            }

                            return (
                              <div>
                                <InputGroup size="sm">
                                  <Input
                                    type="number"
                                    placeholder={unit === WorkCategoryUnit.Pieces ? 'Количество' : 'Сумма'}
                                    min="0"
                                    step="1"
                                    value={record.quantity}
                                    onChange={(e) => this.updateWorkRecord(index, 'quantity', this.sanitizeNonNegativeInt(e.target.value))}
                                    invalid={!!formErrors[`workRecord_${index}_amount`]}
                                  />
                                  <span className="input-group-text">{unitMeta.inputLabel}</span>
                                </InputGroup>
                                {formErrors[`workRecord_${index}_amount`] && (
                                  <div className="invalid-feedback d-block">{formErrors[`workRecord_${index}_amount`]}</div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="align-middle text-end">
                          {Math.round(this.calculateRecordAmount(record, formData.employeeId)).toLocaleString()} ₽
                        </td>
                        <td>
                          <Input
                            type="text"
                            placeholder="Описание..."
                            value={record.comment}
                            onChange={(e) => this.updateWorkRecord(index, 'comment', e.target.value)}
                            size="sm"
                          />
                        </td>
                        <td className="text-center">
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => this.removeWorkRecord(index)}
                            title="Удалить запись"
                          >
                            ×
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {formData.workRecords.length > 0 && formData.employeeId && (
                    <tfoot className="table-light">
                      <tr>
                        <td colSpan="3" className="text-end fw-bold">Итого:</td>
                        <td className="text-end fw-bold">
                          {Math.round(this.calculateTotalAmount(formData.workRecords, formData.employeeId)).toLocaleString()} ₽
                        </td>
                        <td colSpan="2"></td>
                      </tr>
                    </tfoot>
                  )}
                </Table>
              )}
            </FormGroup>

            <FormGroup className="mt-4">
              <Label for="notes">Заметки</Label>
              <Input
                type="textarea"
                name="notes"
                id="notes"
                value={formData.notes}
                onChange={this.handleInputChange}
                rows="3"
                placeholder="Дополнительные заметки к акту..."
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={this.saveWorkAct}>
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
          <Button color="secondary" onClick={this.closeModals}>
            Отмена
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  render() {
    const {
      workActs,
      employees,
      workCategories,
      loading,
      error,
      successMessage,
      createModal,
      editModal,
      viewModal,
      formData,
      formErrors,
      searchTerm,
      statusFilter,
      hideUnpaid,
      page,
      pageSize,
      totalCount
    } = this.state;

    if (loading) {
      return (
        <Container className="mt-5">
          <div className="text-center">Загрузка актов работ...</div>
        </Container>
      );
    }

    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const currentPage = Math.min(page, totalPages);

    return (
      <Container className="mt-5">
        <Row>
          <Col>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">📋 Акты выполненных работ</h3>
                <Button color="primary" onClick={this.openCreateModal}>
                  ➕ Создать акт
                </Button>
              </CardHeader>
              <CardBody>
                {error && <Alert color="danger">{error}</Alert>}
                {successMessage && <Alert color="success">{successMessage}</Alert>}
                
                {/* Filters */}
                <Row className="mb-3 align-items-center">
                  <Col md={5}>
                    <InputGroup>
                      <Input
                        type="text"
                        placeholder="Поиск по номеру или заметке акта..."
                        value={searchTerm}
                        onChange={(e) => this.onSearchChange(e.target.value)}
                      />
                      <Button color="outline-secondary" onClick={() => { this.setState({ searchTerm: '' }); this.reloadFromFirstPage(); }}>
                        ✖️ Очистить
                      </Button>
                    </InputGroup>
                  </Col>
                  <Col md={3}>
                    <Input
                      type="select"
                      value={statusFilter}
                      onChange={(e) => this.setState({ statusFilter: e.target.value }, () => this.reloadFromFirstPage())}
                    >
                      <option value="">Все статусы</option>
                      <option value="0">Черновик</option>
                      <option value="1">Сформирован</option>
                      <option value="2">Оплачен</option>
                    </Input>
                  </Col>
                  <Col md={2}>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="hideUnpaidToggle"
                        checked={hideUnpaid}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          try { window.localStorage.setItem(HIDE_UNPAID_STORAGE_KEY, String(checked)); } catch { /* ignore */ }
                          this.setState({ hideUnpaid: checked }, () => this.reloadFromFirstPage());
                        }}
                      />
                      <label className="form-check-label" htmlFor="hideUnpaidToggle">
                        Скрывать оплаченные
                      </label>
                    </div>
                  </Col>
                  <Col md={2}>
                    <Input
                      type="select"
                      value={pageSize}
                      onChange={(e) => {
                        const next = parseInt(e.target.value, 10);
                        try { window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(next)); } catch { /* ignore */ }
                        this.setState({ pageSize: next }, () => this.reloadFromFirstPage());
                      }}
                    >
                      {PAGE_SIZE_OPTIONS.map(n => (
                        <option key={n} value={n}>{n} на странице</option>
                      ))}
                    </Input>
                  </Col>
                </Row>

                {workActs.length === 0 ? (
                  <div className="text-center text-muted">
                    {searchTerm || statusFilter || hideUnpaid ? 'Акты не найдены' : 'Нет актов работ'}
                  </div>
                ) : (
                  <>
                  <Table responsive striped>
                    <thead>
                      <tr>
                        <th style={{ backgroundColor: '#343a40', color: 'white' }}>📋 Номер акта</th>
                        <th style={{ backgroundColor: '#343a40', color: 'white' }}>👤 Сотрудник</th>
                        <th style={{ backgroundColor: '#343a40', color: 'white' }}>📅 Дата акта</th>
                        <th style={{ backgroundColor: '#343a40', color: 'white' }}>⏱️ Часы</th>
                        <th style={{ backgroundColor: '#343a40', color: 'white' }}>💰 Сумма</th>
                        <th style={{ backgroundColor: '#343a40', color: 'white' }}>🔄 Статус</th>
                        <th style={{ backgroundColor: '#343a40', color: 'white', width: '380px' }}>⚙️ Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workActs.map(workAct => (
                        <tr key={workAct.id}>
                          <td>
                            <strong>{workAct.actNumber}</strong>
                          </td>
                          <td>{workAct.employee?.fullName || 'Неизвестно'}</td>
                          <td>
                            {this.formatDate(workAct.actDate)}
                          </td>
                          <td>{workAct.totalHours?.toFixed(1)} ч</td>
                          <td>{Math.round(workAct.totalAmount || 0).toLocaleString()} ₽</td>
                          <td>{this.getStatusBadge(workAct.status)}</td>
                          <td>
                            <div className="btn-group" role="group">
                              <Button
                                size="sm"
                                style={customStyles.button.view}
                                onClick={() => this.openViewModal(workAct)}
                              >
                                👁️ Просмотр
                              </Button>
                              <Button
                                size="sm"
                                style={customStyles.button.edit}
                                onClick={() => this.openEditModal(workAct)}
                              >
                                ✏️ Изменить
                              </Button>
                              {workAct.status === 0 && (
                                <Button
                                  size="sm"
                                  style={customStyles.button.form}
                                  onClick={() => this.changeStatus(workAct.id, 1)}
                                >
                                  📋 Сформировать
                                </Button>
                              )}
                              {workAct.status === 1 && (
                                <Button
                                  size="sm"
                                  style={customStyles.button.pay}
                                  onClick={() => this.changeStatus(workAct.id, 2)}
                                >
                                  💰 Оплатить
                                </Button>
                              )}
                              <Button
                                size="sm"
                                style={customStyles.button.recalculate}
                                onClick={() => this.recalculateWorkAct(workAct.id)}
                                title="Пересчитать с актуальными ставками"
                              >
                                🔄 Пересчитать
                              </Button>
                              <Button
                                size="sm"
                                style={customStyles.button.delete}
                                onClick={() => this.deleteWorkAct(workAct.id)}
                              >
                                🗑️ Удалить
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <small className="text-muted">
                        Показано {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, totalCount)} из {totalCount}
                      </small>
                      <Pagination size="sm" className="mb-0">
                        <PaginationItem disabled={currentPage === 1}>
                          <PaginationLink first onClick={() => this.goToPage(1)} />
                        </PaginationItem>
                        <PaginationItem disabled={currentPage === 1}>
                          <PaginationLink previous onClick={() => this.goToPage(currentPage - 1)} />
                        </PaginationItem>
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                          .reduce((acc, p, idx, arr) => {
                            if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                            acc.push(p);
                            return acc;
                          }, [])
                          .map((p, i) => p === '…' ? (
                            <PaginationItem key={`gap-${i}`} disabled>
                              <PaginationLink>…</PaginationLink>
                            </PaginationItem>
                          ) : (
                            <PaginationItem key={p} active={p === currentPage}>
                              <PaginationLink onClick={() => this.goToPage(p)}>{p}</PaginationLink>
                            </PaginationItem>
                          ))}
                        <PaginationItem disabled={currentPage === totalPages}>
                          <PaginationLink next onClick={() => this.goToPage(currentPage + 1)} />
                        </PaginationItem>
                        <PaginationItem disabled={currentPage === totalPages}>
                          <PaginationLink last onClick={() => this.goToPage(totalPages)} />
                        </PaginationItem>
                      </Pagination>
                    </div>
                  )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* View Modal */}
        {this.renderViewModal()}

        {/* Create/Edit Modal */}
        {this.renderWorkActModal()}
      </Container>
    );
  }
}

export default WorkActManagementWithAuth;
